import type {
  AuthorProfitAggregatesResponse,
  AuthorProfitResponse,
  AuthorProfitUnitBucket,
  AuthorProfitWindow,
  AuthorProfitWindowCalcBase,
} from '../client/models';
import { ROI_SCORE_LABEL_TOOLTIP } from '../constants/profitDisclaimer';

export { ROI_SCORE_LABEL_TOOLTIP };

export type AuthorProfitConfig = {
  unitSize: number;
  standardOdds: number;
  highDifficultyOdds: number;
  defaultParlayOdds: number;
  useUnitSizing: boolean;
};

export type AuthorProfitWindowKey =
  | 'all_time'
  | 'calendar_year'
  | 'calendar_month'
  | 'eastern_yesterday'
  | 'last_7d';

/** identity = combined linked profile; presence = single platform row */
export type ProfitScope = 'identity' | 'presence';

export type ParlayOddsTier = 'standard' | 'longshot';

export const DEFAULT_AUTHOR_PROFIT_CONFIG: AuthorProfitConfig = {
  unitSize: 10,
  standardOdds: -115,
  highDifficultyOdds: 300,
  defaultParlayOdds: 300,
  useUnitSizing: false,
};

const DEFAULT_UNIT_MULTIPLIER = 1;
const GIMMIE_SINGLE_AMERICAN = -1000;

const HIGH_DIFFICULTY_CATEGORIES = new Set([
  'homeruns',
  'home_runs',
  'home_run',
  'hr',
  't_d',
  'triple_double',
  'triple_doubles',
  'total_tds',
  'td',
  'touchdown',
  'touchdowns',
  'rec_touchdowns',
  'rush_touchdowns',
  'pass_tds',
  'goals',
  'goal',
  'hat_trick',
  'anytime_goal',
  'first_goal',
  'first_goal_scorer',
  'anytime_goal_scorer',
]);

export function formatEstimatedProfit(value: number): string {
  const sign = value >= 0 ? '+' : '-';
  return `${sign}$${Math.abs(value).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
}

/** Display ROI% from backend ``roi_pct`` (profit / staked × 100). */
export function formatRoiPct(roiPct: number | null | undefined): string {
  if (roiPct == null || !Number.isFinite(roiPct)) return '—';
  const sign = roiPct >= 0 ? '+' : '';
  return `${sign}${roiPct.toFixed(1)}%`;
}

/** Projected return per $100 staked at the given ROI%. */
export function formatReturnPer100(roiPct: number | null | undefined): string {
  if (roiPct == null || !Number.isFinite(roiPct)) return '—';
  const dollars = (roiPct / 100) * 100;
  const sign = dollars >= 0 ? '+' : '-';
  return `${sign}$${Math.abs(dollars).toFixed(2)}`;
}

/**
 * Cumulative units if every settled pick is 1u: (ROI% / 100) × picks.
 * Marketing-facing compact form, e.g. ``+400u``.
 */
export function formatTotalUnitsFromRoi(
  roiPct: number | null | undefined,
  totalPicks: number | null | undefined,
): string {
  if (
    roiPct == null ||
    !Number.isFinite(roiPct) ||
    totalPicks == null ||
    !Number.isFinite(totalPicks) ||
    totalPicks <= 0
  ) {
    return '—';
  }
  const units = (roiPct / 100) * totalPicks;
  const sign = units >= 0 ? '+' : '-';
  const abs = Math.abs(units);
  const body = abs >= 100 ? abs.toFixed(0) : abs.toFixed(1);
  return `${sign}${body}u`;
}

/** PCS ``profit_roi_raw`` is a decimal ratio; hero rows may already store ROI%. */
export function profitRoiRawToRoiPct(
  profitRoiRaw: number | null | undefined,
): number | null {
  if (profitRoiRaw == null || !Number.isFinite(Number(profitRoiRaw)))
    return null;
  const v = Number(profitRoiRaw);
  if (Math.abs(v) <= 3) {
    return Math.round(v * 100 * 100) / 100;
  }
  return Math.round(v * 100) / 100;
}

export function resolvedRealizedRoiPct(
  row: { roi_pct?: number | null; profit_roi_raw?: number | null },
  metrics?: { roiPctRaw?: number | null; roiPct?: number | null } | null,
): number | null {
  if (
    metrics?.roiPctRaw != null &&
    Number.isFinite(Number(metrics.roiPctRaw))
  ) {
    return Number(metrics.roiPctRaw);
  }
  if (row.roi_pct != null && Number.isFinite(Number(row.roi_pct))) {
    return Number(row.roi_pct);
  }
  const fromRaw = profitRoiRawToRoiPct(row.profit_roi_raw);
  if (fromRaw != null) return fromRaw;
  if (metrics?.roiPct != null && Number.isFinite(Number(metrics.roiPct))) {
    return Number(metrics.roiPct);
  }
  return null;
}

/** Table/header label for modeled ROI (dollar framing still used on some non-home surfaces). */
export const ROI_PER_100_LABEL = 'ROI per $100';

/** Score-box headline when showing formatRoiPct (+X.X%). */
export const ROI_SCORE_LABEL = 'ROI';

export function tailProfitScoreBoxLabels(roiPct: number | null | undefined): {
  scoreLabel: string;
  labelTooltip?: string;
} {
  if (roiPct != null && Number.isFinite(Number(roiPct))) {
    return {
      scoreLabel: ROI_SCORE_LABEL,
      labelTooltip: ROI_SCORE_LABEL_TOOLTIP,
    };
  }
  return { scoreLabel: 'Est.' };
}

/** Default PR linear map params (mirrors backend ``ProfitRatingParams`` v3). */
export const DEFAULT_PROFIT_RATING_PARAMS = {
  breakEvenScore: 50,
  ceilingScore: 99,
  floorScore: 1,
  roiAtCeiling: 200,
  roiAtFloor: -100,
  minPicks: 10,
} as const;

export type ProfitRatingMapParams = typeof DEFAULT_PROFIT_RATING_PARAMS;

/** Map raw modeled ROI% to 0-99 PR; 50 = break-even (same formula as backend). */
export function roiPctToProfitRating(
  roiPct: number | null | undefined,
  params: ProfitRatingMapParams = DEFAULT_PROFIT_RATING_PARAMS,
): number | null {
  if (roiPct == null || !Number.isFinite(roiPct)) return null;
  const be = params.breakEvenScore;
  const ceilScore = params.ceilingScore;
  const floorScore = params.floorScore;
  if (roiPct >= 0) {
    const raw = be + (roiPct * (ceilScore - be)) / params.roiAtCeiling;
    return Math.round(Math.max(be, Math.min(ceilScore, raw)) * 100) / 100;
  }
  const raw = be + (roiPct * (be - floorScore)) / Math.abs(params.roiAtFloor);
  return Math.round(Math.max(floorScore, Math.min(be, raw)) * 100) / 100;
}

export function profitRatingFromWindowRoi(
  roiPct: number | null | undefined,
  settledPicks: number | null | undefined,
  params: ProfitRatingMapParams = DEFAULT_PROFIT_RATING_PARAMS,
): number | null {
  const settled = Number(settledPicks ?? 0);
  if (settled < params.minPicks) return null;
  return roiPctToProfitRating(roiPct, params);
}

/** Credibility ``profit_roi`` is a ratio (0.048 = 4.8%); ratings use ``roi_pct``. */
export function storedProfitRoiToRoiPct(
  profitRoi: number | null | undefined,
): number | null {
  if (profitRoi == null || !Number.isFinite(Number(profitRoi))) return null;
  return Math.round(Number(profitRoi) * 100 * 100) / 100;
}

/** PR blend params beyond the linear map (mirrors backend v4/v5 defaults). */
export const PR_BLEND_PARAMS = {
  singlesMinPicks: 10,
  parlayMinPicks: 15,
  singlesShrinkageC: 0,
  parlayShrinkageC: 20,
  shadowShrinkageC: 25,
  eprAlphaFloor: 0.3,
} as const;

export function shrinkRoiPct(
  roiPct: number | null | undefined,
  settledN: number,
  shrinkageC: number,
): number | null {
  if (roiPct == null || !Number.isFinite(roiPct) || settledN <= 0) return null;
  if (shrinkageC <= 0) return Math.round(roiPct * 100) / 100;
  const weight = settledN / (settledN + shrinkageC);
  return Math.round(roiPct * weight * 100) / 100;
}

export type HeadlineProfitRatings = {
  rating: number | null;
  roiPct: number | null;
  roiPctRaw: number | null;
  expectedRating: number | null;
  settledPicks: number | null;
  reliabilityWeight: number | null;
};

function profitReliabilityWeight(
  settledN: number,
  shrinkageC: number,
): number | null {
  if (settledN <= 0) return null;
  if (shrinkageC <= 0) return 1;
  return Math.round((settledN / (settledN + shrinkageC)) * 10000) / 10000;
}

/** Blended window PR/ROI from total slice (matches backend ``profit_rating_from_calc_base``). */
export function blendedProfitRatingsFromWindow(
  window: AuthorProfitWindow | null | undefined,
  minPicks: number = DEFAULT_PROFIT_RATING_PARAMS.minPicks,
  params: ProfitRatingMapParams = DEFAULT_PROFIT_RATING_PARAMS,
  shrinkageC: number = PR_BLEND_PARAMS.singlesShrinkageC,
): HeadlineProfitRatings {
  if (!window) {
    return {
      rating: null,
      roiPct: null,
      roiPctRaw: null,
      expectedRating: null,
      settledPicks: null,
      reliabilityWeight: null,
    };
  }
  const settled =
    Number(window.total_wins ?? 0) + Number(window.total_losses ?? 0);
  if (settled < minPicks) {
    return {
      rating: null,
      roiPct: null,
      roiPctRaw: null,
      expectedRating: null,
      settledPicks: settled,
      reliabilityWeight: null,
    };
  }
  const roiPctRaw =
    window.total_roi_pct != null &&
    Number.isFinite(Number(window.total_roi_pct))
      ? Number(window.total_roi_pct)
      : null;
  if (roiPctRaw == null) {
    return {
      rating: null,
      roiPct: null,
      roiPctRaw: null,
      expectedRating: null,
      settledPicks: settled,
      reliabilityWeight: null,
    };
  }
  const shrunkRoi = shrinkRoiPct(roiPctRaw, settled, shrinkageC);
  const shadowRoi = shrinkRoiPct(
    roiPctRaw,
    settled,
    PR_BLEND_PARAMS.shadowShrinkageC,
  );
  return {
    rating: roiPctToProfitRating(shrunkRoi, params),
    roiPct: shrunkRoi,
    roiPctRaw,
    expectedRating:
      shadowRoi != null ? roiPctToProfitRating(shadowRoi, params) : null,
    settledPicks: settled,
    reliabilityWeight: profitReliabilityWeight(settled, shrinkageC),
  };
}

/** Minimum settled picks for PR on eastern_yesterday (matches backend ``_MIN_PICKS_YESTERDAY_ALL``). */
export const YESTERDAY_PR_MIN_PICKS = 3;

/** Headline PR + ROI from full-pool window totals (legacy blended; matches backend). */
export function headlineProfitRatingsFromWindow(
  window: AuthorProfitWindow | null | undefined,
  minPicks: number = DEFAULT_PROFIT_RATING_PARAMS.minPicks,
  params: ProfitRatingMapParams = DEFAULT_PROFIT_RATING_PARAMS,
): HeadlineProfitRatings {
  return blendedProfitRatingsFromWindow(window, minPicks, params);
}

/** SPS from a profit window singles slice (matches backend ``singles_pr_from_breakdown``). */
export function singlesProfitRatingFromWindow(
  window: AuthorProfitWindow | null | undefined,
  params: ProfitRatingMapParams = DEFAULT_PROFIT_RATING_PARAMS,
): number | null {
  if (!window) return null;
  const settled =
    Number(window.singles_wins ?? 0) + Number(window.singles_losses ?? 0);
  if (settled < PR_BLEND_PARAMS.singlesMinPicks) return null;
  const roiPctRaw =
    window.singles_roi_pct != null &&
    Number.isFinite(Number(window.singles_roi_pct))
      ? Number(window.singles_roi_pct)
      : null;
  if (roiPctRaw == null) return null;
  const shrunk = shrinkRoiPct(
    roiPctRaw,
    settled,
    PR_BLEND_PARAMS.singlesShrinkageC,
  );
  return roiPctToProfitRating(shrunk, params);
}

/** PPS from a profit window parlay slice (matches backend ``parlay_pr_from_breakdown``). */
export function parlayProfitRatingFromWindow(
  window: AuthorProfitWindow | null | undefined,
  params: ProfitRatingMapParams = DEFAULT_PROFIT_RATING_PARAMS,
): number | null {
  if (!window) return null;
  const settled =
    Number(window.parlay_wins ?? 0) + Number(window.parlay_losses ?? 0);
  if (settled < PR_BLEND_PARAMS.parlayMinPicks) return null;
  const roiPctRaw =
    window.parlay_roi_pct != null &&
    Number.isFinite(Number(window.parlay_roi_pct))
      ? Number(window.parlay_roi_pct)
      : null;
  if (roiPctRaw == null) return null;
  const shrunk = shrinkRoiPct(
    roiPctRaw,
    settled,
    PR_BLEND_PARAMS.parlayShrinkageC,
  );
  return roiPctToProfitRating(shrunk, params);
}

/** Inverse of ``roiPctToProfitRating`` — approximate ROI% for a PR score. */
export function profitRatingToRoiPct(
  rating: number | null | undefined,
  params: ProfitRatingMapParams = DEFAULT_PROFIT_RATING_PARAMS,
): number | null {
  if (rating == null || !Number.isFinite(rating)) return null;
  const be = params.breakEvenScore;
  const ceilScore = params.ceilingScore;
  const floorScore = params.floorScore;
  const r = Number(rating);
  if (r >= be) {
    const roi = ((r - be) * params.roiAtCeiling) / (ceilScore - be);
    return Math.round(roi * 100) / 100;
  }
  const roi = ((r - be) * Math.abs(params.roiAtFloor)) / (be - floorScore);
  return Math.round(roi * 100) / 100;
}

export function estimatedProfitTextColor(
  value: number | null | undefined,
  muted: string,
): string {
  if (value == null || !Number.isFinite(value)) return muted;
  if (value > 0) return 'green.500';
  if (value < 0) return 'red.500';
  return muted;
}

function profitTotalFromWindowCalcBase(
  aggregates: AuthorProfitAggregatesResponse,
  window: AuthorProfitWindowKey,
  config: AuthorProfitConfig,
): number {
  const calcBase = aggregates[window];
  return profitFromCalcBase(
    calcBase,
    config.unitSize,
    config.standardOdds,
    config.highDifficultyOdds,
    config.defaultParlayOdds,
    config.useUnitSizing,
  ).total_profit;
}

export function profitWindowTotalFromAggregates(
  aggregates: AuthorProfitAggregatesResponse,
  window: AuthorProfitWindowKey,
  config: AuthorProfitConfig = DEFAULT_AUTHOR_PROFIT_CONFIG,
): number {
  return profitTotalFromWindowCalcBase(aggregates, window, config);
}

/** Map ratings UI time window to a persisted profit aggregate key, when one exists. */
export function ratingsTimeWindowToProfitWindow(
  timeWindow?: string | null,
): AuthorProfitWindowKey | null {
  const t = String(timeWindow ?? '')
    .trim()
    .toLowerCase();
  if (!t || t === 'all') return 'all_time';
  if (t === 'calendar_month' || t === 'this_month') return 'calendar_month';
  if (t === 'yesterday') return 'eastern_yesterday';
  if (t === '7d') return 'last_7d';
  if (t === '1y') return 'calendar_year';
  return null;
}

export function heroLeaderboardProfitLookupKey(
  row: {
    ww_author_slug?: string | null;
    author_id?: string | null;
  },
  profitScope: ProfitScope = 'presence',
): string | null {
  if (profitScope === 'presence') {
    const authorId = row.author_id?.trim();
    if (authorId) return authorId;
  }
  const slug = row.ww_author_slug?.trim();
  if (slug) return slug;
  const authorId = row.author_id?.trim();
  if (authorId) return authorId;
  return null;
}

function americanToDecimal(american: number): number {
  if (american === 0) {
    throw new Error('American odds cannot be zero');
  }
  if (american > 0) {
    return 1 + american / 100;
  }
  return 1 + 100 / Math.abs(american);
}

function decimalToAmerican(decimalOdds: number): number {
  if (decimalOdds <= 1) {
    throw new Error('Decimal odds must be greater than 1.0');
  }
  if (decimalOdds >= 2) {
    return Math.round((decimalOdds - 1) * 100);
  }
  return Math.round(-100 / (decimalOdds - 1));
}

export function impliedParlayAmericanOdds(
  perLegAmerican: number,
  legCount: number,
): number {
  if (legCount < 1) {
    throw new Error('legCount must be at least 1');
  }
  const decimalPerLeg = americanToDecimal(perLegAmerican);
  const parlayDecimal = decimalPerLeg ** legCount;
  return decimalToAmerican(parlayDecimal);
}

// Match backend ``parlay_odds_utils._MAX_IMPLIED_PARLAY_ODDS_WITHOUT_OCR`` (v4).
const MAX_IMPLIED_PARLAY_ODDS_WITHOUT_OCR = 15_000;
const MAX_LEGS_TRUST_IMPLIED_WITHOUT_OCR = 4;
const MEGA_LOW_VALUE_PROFIT_MIN_LEGS = 16;
const MEGA_LOW_VALUE_PROFIT_MAX_ODDS = 100;

const MAX_AMERICAN_PARLAY_ODDS_BY_LEGS: Record<number, number> = {
  2: 30_000,
  3: 250_000,
  4: 400_000,
  5: 500_000,
};
const GLOBAL_MAX_AMERICAN_PARLAY_ODDS = 500_000;

function maxPlausibleAmericanParlayOdds(
  legCount: number | null | undefined,
): number {
  const legs = legCount ?? 0;
  if (legs <= 1) {
    return 5_000;
  }
  return (
    MAX_AMERICAN_PARLAY_ODDS_BY_LEGS[legs] ?? GLOBAL_MAX_AMERICAN_PARLAY_ODDS
  );
}

function trustStoredParlayAmerican(
  storedAmerican: number | null | undefined,
  legCount: number | null | undefined,
): number | null {
  if (storedAmerican == null || storedAmerican === 0) {
    return null;
  }
  const cap = maxPlausibleAmericanParlayOdds(legCount);
  if (storedAmerican > 0 && storedAmerican > cap) {
    return null;
  }
  return storedAmerican;
}

/**
 * Match backend ``should_use_implied_parlay_profit_odds``: never compound from
 * metadata ``leg_count`` alone (requires per-leg tiers). Incomplete tier lists
 * vs claimed leg count are rejected in ``effectiveParlayAmericanOdds``.
 */
export function shouldUseImpliedParlayProfitOdds(
  _legCount: number | null | undefined,
  perLegOddsTiers: string[] | null | undefined,
  allLegsLowValue = false,
): boolean {
  if (!perLegOddsTiers || perLegOddsTiers.length < 2) {
    return false;
  }
  const count = perLegOddsTiers.length;
  if (count <= MAX_LEGS_TRUST_IMPLIED_WITHOUT_OCR) {
    return true;
  }
  return allLegsLowValue;
}

/** Match backend ``parlay_profile_incomplete_for_profit`` (tiers-only path). */
export function parlayProfileIncompleteForProfit(
  metadataLegCount: number | null | undefined,
  perLegOddsTiers: string[] | null | undefined,
): boolean {
  if (metadataLegCount == null || metadataLegCount < 2) {
    return false;
  }
  if (perLegOddsTiers && metadataLegCount > perLegOddsTiers.length) {
    return true;
  }
  return false;
}

function capParlayTiersToLegCount(
  tiers: string[] | null | undefined,
  legCount: number | null | undefined,
): string[] | null {
  if (!tiers || tiers.length === 0) {
    return null;
  }
  if (legCount == null || legCount < 2 || tiers.length <= legCount) {
    return tiers;
  }
  return tiers.slice(0, legCount);
}

export function capLongLowValueParlayProfitOdds(
  implied: number,
  legCount: number,
  isAllLowValue: boolean,
  defaultParlayOdds: number,
  isAllEasy = false,
): number {
  // Match backend ``cap_long_low_value_parlay_profit_odds``.
  if (!(isAllLowValue || isAllEasy)) {
    return implied;
  }
  if (legCount >= MEGA_LOW_VALUE_PROFIT_MIN_LEGS) {
    return Math.min(implied, MEGA_LOW_VALUE_PROFIT_MAX_ODDS);
  }
  return Math.min(implied, defaultParlayOdds);
}

export function impliedParlayAmericanFromLegTiers(
  perLegTiers: ParlayOddsTier[],
  standardOdds: number,
  highDifficultyOdds: number,
): number {
  let decimal = 1;
  for (const tier of perLegTiers) {
    const legAmerican = tier === 'longshot' ? highDifficultyOdds : standardOdds;
    decimal *= americanToDecimal(legAmerican);
  }
  const american = decimalToAmerican(decimal);
  const cap = Math.min(
    maxPlausibleAmericanParlayOdds(perLegTiers.length),
    MAX_IMPLIED_PARLAY_ODDS_WITHOUT_OCR,
  );
  return Math.min(american, cap);
}

const MODELLED_PARLAY_ODDS_PROVENANCES = new Set([
  'adjusted_from_leg_odds',
  'implied',
]);
// Keep provenance + gimmie override in sync with wagerwatchdog/backend/app/parlay_odds_utils.py

export function effectiveParlayAmericanOdds(
  storedAmerican: number | null | undefined,
  legCount: number | null | undefined,
  perLegAmerican: number,
  defaultParlayOdds: number,
  highDifficultyOdds: number,
  perLegOddsTiers?: string[] | null,
  allLegsLowValue = false,
  allLegsEasy = false,
  hasGimmieLeg = false,
  storedProvenance?: string | null,
): number {
  const trustedStored = trustStoredParlayAmerican(storedAmerican, legCount);
  if (trustedStored != null) {
    if (
      hasGimmieLeg &&
      storedProvenance &&
      MODELLED_PARLAY_ODDS_PROVENANCES.has(storedProvenance)
    ) {
      return defaultParlayOdds;
    }
    return trustedStored;
  }

  if (hasGimmieLeg && !allLegsLowValue) {
    return defaultParlayOdds;
  }

  const cappedTiers = capParlayTiersToLegCount(perLegOddsTiers, legCount);
  if (parlayProfileIncompleteForProfit(legCount, cappedTiers)) {
    return defaultParlayOdds;
  }

  const tiers =
    cappedTiers && cappedTiers.length >= 2
      ? (cappedTiers as ParlayOddsTier[])
      : null;

  // ponytail: never compound from leg_count alone — matches backend.
  if (!tiers) {
    return defaultParlayOdds;
  }

  const useImplied = shouldUseImpliedParlayProfitOdds(
    legCount,
    tiers,
    allLegsLowValue,
  );
  if (!useImplied) {
    return defaultParlayOdds;
  }

  const implied = impliedParlayAmericanFromLegTiers(
    tiers,
    perLegAmerican,
    highDifficultyOdds,
  );

  return capLongLowValueParlayProfitOdds(
    implied,
    tiers.length,
    allLegsLowValue,
    defaultParlayOdds,
    allLegsEasy,
  );
}

function profitForOutcome(
  isWin: boolean,
  unitSize: number,
  americanOdds: number,
): number {
  if (isWin) {
    const decimalOdds = americanToDecimal(americanOdds);
    return Math.round(unitSize * (decimalOdds - 1) * 100) / 100;
  }
  return Math.round(-unitSize * 100) / 100;
}

function effectivePickUnitMultiplier(
  statedMultiplier: number,
  useUnitSizing: boolean,
): number {
  if (!useUnitSizing) {
    return DEFAULT_UNIT_MULTIPLIER;
  }
  return statedMultiplier;
}

function profitFromSinglesBuckets(
  buckets: AuthorProfitUnitBucket[] | undefined,
  unitSize: number,
  americanOdds: number,
  useUnitSizing: boolean,
): { profit: number; wins: number; losses: number } {
  let profit = 0;
  let wins = 0;
  let losses = 0;

  for (const bucket of buckets ?? []) {
    const stated = bucket.unit_multiplier ?? DEFAULT_UNIT_MULTIPLIER;
    const pickMult = effectivePickUnitMultiplier(stated, useUnitSizing);
    const effectiveUnit = Math.round(unitSize * pickMult * 100) / 100;
    const bucketWins = bucket.wins ?? 0;
    const bucketLosses = bucket.losses ?? 0;

    for (let i = 0; i < bucketWins; i += 1) {
      profit += profitForOutcome(true, effectiveUnit, americanOdds);
      wins += 1;
    }
    for (let i = 0; i < bucketLosses; i += 1) {
      profit += profitForOutcome(false, effectiveUnit, americanOdds);
      losses += 1;
    }
  }

  return {
    profit: Math.round(profit * 100) / 100,
    wins,
    losses,
  };
}

const EXTREME_PARLAY_AMERICAN_THRESHOLD = 10_000;
const EXTREME_PARLAY_CAP_AMERICAN = 10_000;

function extremeParlayExcessPnl(
  pnl: number,
  american: number,
  effectiveUnit: number,
): number {
  if (pnl <= 0 || american <= EXTREME_PARLAY_AMERICAN_THRESHOLD) {
    return 0;
  }
  const capped = profitForOutcome(
    true,
    effectiveUnit,
    EXTREME_PARLAY_CAP_AMERICAN,
  );
  return Math.round(Math.max(0, pnl - capped) * 100) / 100;
}

function sliceRoiPct(
  profit: number,
  wins: number,
  losses: number,
  unitSize: number,
): number | null {
  const settled = wins + losses;
  if (settled <= 0 || unitSize <= 0) {
    return null;
  }
  return Math.round((profit / (unitSize * settled)) * 100 * 100) / 100;
}

function profitFromCalcBase(
  calcBase: AuthorProfitWindowCalcBase,
  unitSize: number,
  standardOdds: number,
  highDifficultyOdds: number,
  defaultParlayOdds: number,
  useUnitSizing: boolean,
): AuthorProfitWindow {
  const standard = profitFromSinglesBuckets(
    calcBase.standard_singles,
    unitSize,
    standardOdds,
    useUnitSizing,
  );
  const longshot = profitFromSinglesBuckets(
    calcBase.longshot_singles,
    unitSize,
    highDifficultyOdds,
    useUnitSizing,
  );
  const gimmie = profitFromSinglesBuckets(
    calcBase.gimmie_singles ?? [],
    unitSize,
    GIMMIE_SINGLE_AMERICAN,
    useUnitSizing,
  );

  const singlesProfit =
    Math.round((standard.profit + longshot.profit + gimmie.profit) * 100) / 100;
  const singlesWins = standard.wins + longshot.wins + gimmie.wins;
  const singlesLosses = standard.losses + longshot.losses + gimmie.losses;

  let parlayProfit = 0;
  let parlayWins = 0;
  let parlayLosses = 0;
  let outlierWins = 0;
  let outlierExcess = 0;

  for (const row of calcBase.parlay_outcomes ?? []) {
    const americanOdds = effectiveParlayAmericanOdds(
      row.american_odds,
      row.leg_count,
      standardOdds,
      defaultParlayOdds,
      highDifficultyOdds,
      row.per_leg_odds_tiers,
      row.all_legs_low_value ?? false,
      row.all_legs_easy ?? false,
      row.has_gimmie_leg ?? false,
      row.stored_odds_provenance,
    );
    const stated = row.unit_multiplier ?? DEFAULT_UNIT_MULTIPLIER;
    const pickMult = effectivePickUnitMultiplier(stated, useUnitSizing);
    const effectiveUnit = Math.round(unitSize * pickMult * 100) / 100;
    const pnl = profitForOutcome(row.is_win, effectiveUnit, americanOdds);
    parlayProfit += pnl;
    if (row.is_win) {
      parlayWins += 1;
      const excess = extremeParlayExcessPnl(pnl, americanOdds, effectiveUnit);
      if (excess > 0) {
        outlierWins += 1;
        outlierExcess = Math.round((outlierExcess + excess) * 100) / 100;
      }
    } else {
      parlayLosses += 1;
    }
  }
  parlayProfit = Math.round((parlayProfit - outlierExcess) * 100) / 100;

  const totalWins = singlesWins + parlayWins;
  const totalLosses = singlesLosses + parlayLosses;

  const totalProfit = Math.round((singlesProfit + parlayProfit) * 100) / 100;

  return {
    singles_profit: singlesProfit,
    singles_wins: singlesWins,
    singles_losses: singlesLosses,
    singles_roi_pct: sliceRoiPct(
      singlesProfit,
      singlesWins,
      singlesLosses,
      unitSize,
    ),
    parlay_profit: parlayProfit,
    parlay_wins: parlayWins,
    parlay_losses: parlayLosses,
    parlay_roi_pct: sliceRoiPct(
      parlayProfit,
      parlayWins,
      parlayLosses,
      unitSize,
    ),
    total_profit: totalProfit,
    total_wins: totalWins,
    total_losses: totalLosses,
    total_roi_pct: sliceRoiPct(totalProfit, totalWins, totalLosses, unitSize),
    parlay_outlier_win_count: outlierWins,
    parlay_outlier_excess_profit: outlierExcess,
    total_profit_ex_outliers: outlierExcess > 0 ? totalProfit : null,
  };
}

export function applyAuthorProfitAssumptions(
  aggregates: AuthorProfitAggregatesResponse,
  config: AuthorProfitConfig,
): AuthorProfitResponse {
  const defaultParlayOdds = config.defaultParlayOdds;

  return {
    all_time: profitFromCalcBase(
      aggregates.all_time,
      config.unitSize,
      config.standardOdds,
      config.highDifficultyOdds,
      defaultParlayOdds,
      config.useUnitSizing,
    ),
    calendar_year: profitFromCalcBase(
      aggregates.calendar_year,
      config.unitSize,
      config.standardOdds,
      config.highDifficultyOdds,
      defaultParlayOdds,
      config.useUnitSizing,
    ),
    calendar_month: profitFromCalcBase(
      aggregates.calendar_month,
      config.unitSize,
      config.standardOdds,
      config.highDifficultyOdds,
      defaultParlayOdds,
      config.useUnitSizing,
    ),
    eastern_yesterday: profitFromCalcBase(
      aggregates.eastern_yesterday,
      config.unitSize,
      config.standardOdds,
      config.highDifficultyOdds,
      defaultParlayOdds,
      config.useUnitSizing,
    ),
    last_7d: profitFromCalcBase(
      aggregates.last_7d,
      config.unitSize,
      config.standardOdds,
      config.highDifficultyOdds,
      defaultParlayOdds,
      config.useUnitSizing,
    ),
    unit_size: config.unitSize,
    standard_odds: config.standardOdds,
    high_difficulty_odds: config.highDifficultyOdds,
    default_parlay_odds: defaultParlayOdds,
  };
}

export function isHighDifficultyCategory(
  category: string | null | undefined,
): boolean {
  const cat = (category ?? '').trim().toLowerCase();
  return HIGH_DIFFICULTY_CATEGORIES.has(cat);
}
